import { formatCalendarDate, formatDateTime } from '@/utils/format';

/**
 * Turns a platform activity snapshot into labeled before → after rows.
 * Raw objects are not shown: an admin should be able to read the change.
 */

export interface PlatformAuditChange {
  key: string;
  label: string;
  before: string | null;
  after: string | null;
}

type Translate = (key: string, options?: Record<string, string | number>) => string;

const CALENDAR_FIELDS = new Set(['trialEndsAt', 'currentPeriodEnd', 'validFrom', 'validUntil']);

const IMAGE_FIELDS = new Set([
  'sermon',
  'devotional',
  'announcement',
  'video',
  'gallery',
  'event',
  'service',
]);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const isHiddenKey = (key: string): boolean =>
  key.endsWith('Id') || key.endsWith('Ref') || key === 'notesCipher';

const humanize = (key: string): string => {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const translate = (t: Translate, key: string, fallback: string): string => {
  const value = t(key, { defaultValue: fallback });
  return value && value !== key ? value : fallback;
};

const isStorageKey = (value: string): boolean =>
  value.includes('/') || (!value.includes(' ') && value.length > 24);

const stringValue = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const flatten = (record: Record<string, unknown> | null): Record<string, unknown> => {
  if (!record) return {};
  const flat: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (isHiddenKey(key)) continue;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [child, childValue] of Object.entries(value as Record<string, unknown>)) {
        if (isHiddenKey(child)) continue;
        flat[child] = childValue;
      }
      continue;
    }
    flat[key] = value;
  }
  return flat;
};

const same = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

const formatScalar = (
  key: string,
  value: unknown,
  sibling: Record<string, unknown>,
  t: Translate,
  empty: string,
): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') {
    return value ? t('audit.values.yes') : t('audit.values.no');
  }
  if (Array.isArray(value)) {
    const parts = value
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map((item) => translate(t, `givingPayments.providers.${item}`, humanize(item)));
    return parts.length > 0 ? parts.join(', ') : null;
  }
  if (typeof value === 'number') {
    if (key === 'idleTimeoutMinutes') return t('audit.values.minutes', { count: value });
    if (key === 'durationMonths') return t('billing.coupons.durations.months', { count: value });
    if (key === 'discountValue') {
      const discountType = stringValue(sibling.discountType);
      if (discountType === 'percent') return t('audit.values.percent', { value });
      if (discountType === 'free_months') {
        return t('billing.coupons.durations.months', { count: value });
      }
    }
    return String(value);
  }
  if (typeof value !== 'string') return null;
  if (UUID_RE.test(value)) return null;
  if (IMAGE_FIELDS.has(key) && isStorageKey(value)) return t('audit.values.pictureSet');
  if (CALENDAR_FIELDS.has(key) || /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return formatCalendarDate(value);
  }
  if (ISO_RE.test(value)) return formatDateTime(value);

  const byField: Record<string, string> = {
    plan: `billing.plans.${value}`,
    platformRole: `platformRoles.${value}`,
    kind: `billing.coupons.kinds.${value}`,
    discountType: `billing.coupons.discountTypes.${value}`,
    durationType: `billing.coupons.durations.${value}`,
    transportMode: `sms.modes.${value}`,
    primaryProvider: `email.providers.${value}`,
    fallbackProvider: `email.providers.${value}`,
  };
  const mapped = byField[key];
  if (mapped) return translate(t, mapped, humanize(value));
  if (key === 'status') {
    return (
      translate(t, `billing.statuses.${value}`, '') ||
      translate(t, `status.${value}`, '') ||
      translate(t, `supportTickets.statuses.${value}`, humanize(value))
    );
  }
  if (isStorageKey(value)) return t('audit.values.pictureSet');
  return value || empty;
};

const fieldLabel = (key: string, t: Translate): string => {
  if (IMAGE_FIELDS.has(key)) return translate(t, `defaultImages.types.${key}`, humanize(key));
  return translate(t, `audit.fields.${key}`, humanize(key));
};

export const formatPlatformAuditChanges = (
  beforeValue: Record<string, unknown> | null,
  afterValue: Record<string, unknown> | null,
  t: Translate,
): PlatformAuditChange[] => {
  const before = flatten(beforeValue);
  const after = flatten(afterValue);
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  const changes: PlatformAuditChange[] = [];

  for (const key of keys) {
    const prev = before[key];
    const next = after[key];
    if (same(prev, next)) continue;

    const sibling = { ...before, ...after };
    let beforeText = formatScalar(key, prev, sibling, t, '');
    let afterText = formatScalar(key, next, sibling, t, '');

    if (
      IMAGE_FIELDS.has(key) &&
      typeof prev === 'string' &&
      typeof next === 'string' &&
      isStorageKey(prev) &&
      isStorageKey(next)
    ) {
      beforeText = t('audit.values.pictureSet');
      afterText = t('audit.values.pictureReplaced');
    }

    if (!beforeText && !afterText) continue;

    changes.push({
      key,
      label: fieldLabel(key, t),
      before: beforeText,
      after: afterText,
    });
  }

  return changes;
};
